import { defineMessages, IntlShape } from "react-intl";

import CalendarViewMode from "@app/enum/CalendarViewMode";
import { getWeekEnd, getWeekStart } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";

const messages = defineMessages({
  day: {
    id: "activities.schedulerView.calendar.toolbar.title.day",
    defaultMessage: "MMM d, yyyy",
    description: "Date format for title scheduler view for activities for day-mode",
  },
  week: {
    id: "activities.schedulerView.calendar.toolbar.title.week",
    defaultMessage: "{start} - {end}, {year}",
    description:
      "Date format for title scheduler view for activities for week-mode when timeframe falls into a single month",
  },
  weekDatesJoiner: {
    id: "activities.schedulerView.calendar.toolbar.title.week.diff",
    defaultMessage: "{start} — {end}",
    description:
      "A string to join start and end parts when week starts in one month/year and ends in another",
  },
  weekEndDifferentMonths: {
    id: "activities.schedulerView.calendar.toolbar.title.week.diffMonths.end",
    defaultMessage: "MMM d, yyyy",
    description:
      "Date format for title scheduler view for activities for week-mode when timeframe starts in one month, but ends in another. Part 2",
  },
  weekEndDifferentYears: {
    id: "activities.schedulerView.calendar.toolbar.title.week.diffYears.end",
    defaultMessage: "MMM d, yyyy",
    description:
      "Date format for title scheduler view for activities for week-mode when timeframe starts in one year, but ends in another. Part 2",
  },
  weekStartDifferentMonths: {
    id: "activities.schedulerView.calendar.toolbar.title.week.diffMonths.start",
    defaultMessage: "MMM d",
    description:
      "Date format for title scheduler view for activities for week-mode when timeframe starts in one month, but ends in another. Part 1",
  },
  weekStartDifferentYears: {
    id: "activities.schedulerView.calendar.toolbar.title.week.diffYears.start",
    defaultMessage: "MMM d, yyyy",
    description:
      "Date format for title scheduler view for activities for week-mode when timeframe starts in one year, but ends in another. Part 1",
  },
});

export const getTitle = (
  intl: IntlShape,
  timeframeStart: Date,
  viewMode: CalendarViewMode
): string => {
  switch (viewMode) {
    case CalendarViewMode.DAY:
      return formatDate(timeframeStart, intl.formatMessage(messages.day));

    case CalendarViewMode.WEEK: {
      const startAt = getWeekStart(timeframeStart);
      const endAt = getWeekEnd(timeframeStart);
      const differentYear = endAt.getFullYear() !== startAt.getFullYear();
      const differentMonth = endAt.getMonth() !== startAt.getMonth();
      if (!differentMonth && !differentYear) {
        return intl.formatMessage(messages.week, {
          end: formatDate(endAt, "d"),
          start: formatDate(startAt, "MMM d"),
          year: formatDate(startAt, "yyyy"),
        });
      }
      const start = formatDate(
        startAt,
        intl.formatMessage(
          differentYear ? messages.weekStartDifferentYears : messages.weekStartDifferentMonths
        )
      );
      const end = formatDate(
        endAt,
        intl.formatMessage(
          differentYear ? messages.weekEndDifferentYears : messages.weekEndDifferentMonths
        )
      );
      return intl.formatMessage(messages.weekDatesJoiner, { end, start });
    }

    default:
      return "";
  }
};
